import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBoundStore } from "@/stores";
import { MUST_COMPLETE_RESET_STATUSES } from "@/features/auth/types";

interface GuestGuardProps {
    children: React.ReactNode;
    loadingFallback?: React.ReactNode;
}

/** Default destination for authenticated users when leaving a guest route (e.g. after login). */
const DEFAULT_AUTH_REDIRECT = "/my-account";

/**
 * Guest Guard - Only allows unauthenticated users.
 * Authenticated users with must-complete-reset status → redirect to /reset-password.
 * Other authenticated users → redirect to from or DEFAULT_AUTH_REDIRECT (so login doesn't send them back to "/").
 */
const GuestGuard: React.FC<GuestGuardProps> = ({
    children,
    loadingFallback
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const isAuthenticated = useBoundStore.use.isAuthenticated();
    const isAuthLoading = useBoundStore.use.isAuthLoading();
    const user = useBoundStore.use.user();
    const accountStatus = useBoundStore.use.accountStatus();

    const from = (location.state as { from?: string })?.from;
    const status = user?.status ?? accountStatus;
    const mustCompleteReset = status != null && MUST_COMPLETE_RESET_STATUSES.includes(status);
    const shouldRedirectToReset = isAuthenticated && mustCompleteReset;
    const shouldRedirectToHome = isAuthenticated && !mustCompleteReset;

    useEffect(() => {
        if (isAuthLoading) return;
        if (shouldRedirectToReset) {
            navigate("/reset-password", { replace: true });
            return;
        }
        if (shouldRedirectToHome) {
            navigate(from || DEFAULT_AUTH_REDIRECT, { replace: true });
        }
    }, [isAuthLoading, shouldRedirectToReset, shouldRedirectToHome, navigate, from]);

    if (isAuthLoading) {
        // Light fallback: GuestGuard skeleton never showed in traces; avoid heavy SkeletonLoader on guest routes.
        return <>{loadingFallback ?? null}</>;
    }

    if (shouldRedirectToReset || shouldRedirectToHome) {
        return null;
    }

    return <>{children}</>;
};

export default GuestGuard;

